/**
 * Select primitive - Direction A "Tu phap restraint"
 *
 * Native <select> styled - NOT custom dropdown (best a11y default).
 * 2 variant: default (40px) | compact (32px).
 * Chevron icon trailing (decorative, aria-hidden).
 *
 * A11y:
 *  - aria-label OR explicit <label> required (caller responsibility)
 *  - native keyboard navigation + screen reader baseline
 *  - placeholder via first <option> disabled selected
 *
 * Phase 1 Track 2 (2026-05-22).
 */

import * as React from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export type SelectVariant = 'default' | 'compact';

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: SelectOption[];
  variant?: SelectVariant;
  placeholder?: string;
}

const ChevronIcon = (): React.ReactElement => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    {
      options,
      variant = 'default',
      placeholder,
      className,
      value,
      defaultValue,
      ...rest
    },
    ref
  ) {
    const cls = [
      'ask-select',
      variant === 'compact' ? 'ask-select--compact' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Placeholder rendering only when no value/defaultValue chosen.
    const hasValue = value !== undefined || defaultValue !== undefined;

    return (
      <span className="ask-select-wrap">
        <select
          ref={ref}
          className={cls}
          value={value}
          defaultValue={hasValue ? defaultValue : placeholder ? '' : undefined}
          {...rest}
        >
          {placeholder ? (
            <option value="" disabled hidden>
              {placeholder}
            </option>
          ) : null}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        <span className="ask-select-wrap__chevron">
          <ChevronIcon />
        </span>
      </span>
    );
  }
);
